/**
 * What a use case may do, by where it came from (DESIGN §8). Built-ins and
 * your own recordings may touch the account, after a confirmation the runner
 * asks for. Imported files and Director drafts are held to the Worlds: they
 * may call only operations about a World and what is in it (plus health,
 * presets and whoami), so never keys, webhooks, billing, sign-up, grants or
 * the operator, and never `mcp` or `assist`, which reach anything. That is a
 * list of what is allowed, not of what is not, so an operation the spec adds
 * later is refused until someone says otherwise. A Director draft may only
 * call operations the spec offers the Director (`x-assist`).
 *
 * For every use case: no step saves a secret, no variable lands in a URL,
 * each step acts as a persona the use case declares, every operation is in
 * the spec, and an `mcp` step names its method and tool plainly, so the tool
 * it calls is counted like the operation it is.
 */

import type { Catalogue, Json, Operation, Step, UseCase, Vet } from '../contracts.ts';
import { isPersonaKey } from '../worlds.ts';
import { capturesSecret, varsIn } from './format.ts';
import { valuesAt } from '../spec/nouns.ts';
import { objectOf } from '../model/json.ts';

const ACCOUNT_TAGS = new Set(['keys', 'webhooks', 'billing', 'signup', 'account']);
const ACCOUNT_OPS = new Set(['giveGrant', 'revokeGrant', 'operatorIssueKey']);

/** An operation that reaches past the Worlds into the account (or the operator's desk). */
export const isAccountOp = (op: Operation | undefined, id: string): boolean =>
  ACCOUNT_OPS.has(id) || (op !== undefined && (op.tags.some((t) => ACCOUNT_TAGS.has(t)) || op.security === 'operator'));

/** Tags whose operations stay inside a World: all an untrusted use case may reach. */
const WORLD_TAGS = new Set(['worlds', 'occupiers', 'door', 'reading', 'relays', 'asks', 'watching']);
/** Reads around the Worlds that change nothing and tell nothing secret. */
const AROUND = new Set(['health', 'presets', 'whoami']);

/** Whether an imported file or a Director draft may call this: only when it stays in the Worlds. */
export const isWorldOp = (op: Operation | undefined, id: string): boolean =>
  op !== undefined && op.security !== 'operator'
  && (AROUND.has(id) || (op.tags.length > 0 && op.tags.every((t) => WORLD_TAGS.has(t))));


/**
 * The tool an `mcp` step calls: its name; null when the step does not name it
 * (or its method) plainly, as a {{variable}} or a batch would; undefined when
 * it calls no tool (initialize, tools/list) or is not an `mcp` step.
 */
export const mcpTool = (s: Step): string | null | undefined => {
  if (s.op !== 'mcp') return undefined;
  const body = s.args.body;
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const method = body.method;
  if (typeof method !== 'string' || varsIn(method).length > 0) return null;
  if (method !== 'tools/call') return undefined;
  const name = objectOf(body.params).name;
  return typeof name === 'string' && varsIn(name).length === 0 ? name : null;
};

/** Why a step needs a yes before a run: it touches the account, or spends one of the month's Director runs. */
const needsYes = (s: Step, catalogue: Catalogue): string | null => {
  const tool = mcpTool(s);
  if (typeof tool === 'string') return isAccountOp(catalogue.byId(tool), tool) ? `${tool} (over MCP)` : null;
  const op = catalogue.byId(s.op);
  if (isAccountOp(op, s.op) || op?.tags.includes('assistant')) return s.op;
  return null;
};

/** The operations a use case calls that need a yes (account, a Director run), once each, in order. */
export const accountOps = (uc: UseCase, catalogue: Catalogue): string[] =>
  [...new Set(uc.steps.map((s) => needsYes(s, catalogue)).filter((id): id is string => id !== null))];

const URLISH = /^(url|uri|href|link|callback)$/i;

/** Keys anywhere in a value that name a URL, and what they hold. */
const urlFields = (value: Json, out: Json[] = []): Json[] => {
  if (Array.isArray(value)) value.forEach((v) => urlFields(v, out));
  else if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) {
      if (URLISH.test(k) || /url$/i.test(k)) out.push(v);
      urlFields(v, out);
    }
  }
  return out;
};

const schemaFormat = (schema: Json): string | null =>
  (schema && typeof schema === 'object' && !Array.isArray(schema) && typeof schema.format === 'string' ? schema.format : null);

const untrusted = (uc: UseCase): boolean => uc.origin === 'imported' || uc.origin === 'director';

const stepReasons = (s: Step, i: number, uc: UseCase, catalogue: Catalogue): string[] => {
  const n = `step ${i + 1} (${s.op})`;
  const reasons: string[] = [];
  const op = catalogue.byId(s.op);
  if (!op) reasons.push(`${n}: no operation "${s.op}" in this spec`);
  const persona = s.as ?? 'A';
  if (!uc.personas.includes(persona)) reasons.push(`${n}: acts as persona ${persona}, which this use case does not declare`);
  for (const path of Object.values(s.save ?? {})) {
    if (capturesSecret(path)) reasons.push(`${n}: saves "${path}", a secret; secrets are shown once and never kept`);
  }
  // A variable must never choose where a request goes.
  const holes = op?.holes.filter((h) => URLISH.test(h.name) || /url$/i.test(h.name) || schemaFormat(h.schema) === 'uri') ?? [];
  const suspects = [...holes.flatMap((h) => valuesAt(s.args as Json, h.pointer)), ...urlFields(s.args as Json)];
  if (suspects.some((v) => varsIn(v).length > 0)) reasons.push(`${n}: puts a {{variable}} into a URL`);
  const tool = mcpTool(s);
  if (tool === null) reasons.push(`${n}: names its MCP method or tool by a {{variable}} (or not at all); it must name them plainly`);
  else if (typeof tool === 'string' && !catalogue.byId(tool)) reasons.push(`${n}: calls no tool "${tool}" this spec has`);
  if (untrusted(uc)) {
    const who = uc.origin === 'imported' ? 'an imported' : 'a Director';
    if (op && !isWorldOp(op, s.op)) {
      reasons.push(`${n}: ${who} use case may not touch keys, webhooks, billing, sign-up, grants, the operator, the Director or raw MCP; only Worlds and what is in them`);
    }
    // Each step speaks as its own persona: a World key naming another's would borrow that persona's key.
    if (typeof s.args.w === 'string' && isPersonaKey(s.args.w)) reasons.push(`${n}: names a World by a persona's key ("${s.args.w}"); say which persona acts with "as"`);
  }
  if (uc.origin === 'director' && op && op.assist === null) reasons.push(`${n}: the Director is not offered this operation`);
  return reasons;
};

export const vet: Vet = (uc, catalogue) => {
  const reasons = uc.steps.flatMap((s, i) => stepReasons(s, i, uc, catalogue));
  if (untrusted(uc)) {
    for (const [name, value] of Object.entries(uc.vars ?? {})) {
      if (isPersonaKey(value)) reasons.push(`variable "${name}" holds a persona's World key ("${value}"); say which persona acts with "as"`);
    }
  }
  return reasons.length === 0 ? { ok: true } : { ok: false, reasons };
};
